import _ from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { NetworkScanState } from '../codegen/graphql';
import { isNetworkScanPassed } from '../config/networkScanConfig';

/**
 * Single home of "may a new IoT connection be attached here?" (IBM-1039).
 *
 * Two layers, deliberately separate:
 *  1. `getIotAttachEntryBlock` — the frontend-only conditions every asset
 *     entry point (route guard, side nav, hero button, table action) applies
 *     identically. They were scattered and drifted apart before; the route
 *     guard's rule is the reference.
 *  2. `resolveIotAttachGate` — the backend flag `canAttachIotConnection`
 *     (AssetLocation / System) plus the effective network scan that explains
 *     a "no". Verified on testing (2026-10-07): the backend folds in ONLY the
 *     GREEN scan and the structural rules (asset inside a gateway system,
 *     non-current location). Contract, industry type, region, product IoT
 *     capability, existing connection and admin role are NOT part of it, so
 *     layer 1 (and the wizard's own pre-checks) stay necessary.
 *
 * Entry points only use layer 1: they stay visible when the scan blocks and
 * the wizard shows the reason (decision 2 of the IBM-1039 interview).
 */

/** JWT roles that may create IoT connections. */
export const IOT_ADMIN_ROLES = ['globalAdmin', 'regionAdmin'] as const;

/** `otto_access.roles` of the Keycloak token contains globalAdmin or regionAdmin. */
export const isIoTAdminToken = (token: string | undefined | null): boolean => {
    if (!token) return false;
    try {
        const decoded = jwtDecode<Record<string, unknown>>(token);
        const roles = _.get(decoded, 'otto_access.roles', []) as string[];
        return roles.some((r) => (IOT_ADMIN_ROLES as readonly string[]).includes(r));
    } catch {
        return false;
    }
};

export interface IotAttachEntryInput {
    isIoTAdmin: boolean;
    /** The asset location belongs to an installed base node. */
    isInstalled: boolean;
    hasIotConnection: boolean;
    /** A connection was just created and is still being polled for. */
    isPendingIotConnection: boolean;
    contractValid: boolean;
    /** Semiconductor customers have no IoT offering. */
    isSemiconductor: boolean;
}

export type IotAttachEntryBlock =
    | 'notIoTAdmin'
    | 'notInstalled'
    | 'hasConnection'
    | 'pendingConnection'
    | 'contractInvalid'
    | 'semiconductor';

/** First frontend condition that hides the "connect" entry, or null when none does. */
export const getIotAttachEntryBlock = (input: IotAttachEntryInput): IotAttachEntryBlock | null => {
    if (!input.isIoTAdmin) return 'notIoTAdmin';
    if (!input.isInstalled) return 'notInstalled';
    if (input.hasIotConnection) return 'hasConnection';
    if (input.isPendingIotConnection) return 'pendingConnection';
    if (!input.contractValid) return 'contractInvalid';
    if (input.isSemiconductor) return 'semiconductor';
    return null;
};

/** The "connect" entry (nav item, button, action, route) is offered. */
export const isIotAttachEntryVisible = (input: IotAttachEntryInput): boolean =>
    getIotAttachEntryBlock(input) === null;

export interface NetworkScanSourceRef {
    installedBaseNodeId: string;
    nodeTypeKey?: string | null;
    name?: string | null;
}

export interface EffectiveNetworkScanRef {
    state?: NetworkScanState | string | null;
    scanDate?: string | null;
    source?: NetworkScanSourceRef | null;
}

export interface IotAttachGateInput {
    /** isSlaEnabled() — with the flag off the gate never blocks. */
    enabled: boolean;
    /** The gate query has settled (data arrived) or was skipped. */
    loaded: boolean;
    canAttachIotConnection?: boolean | null;
    effectiveNetworkScan?: EffectiveNetworkScanRef | null;
}

/**
 * Why the backend says no:
 *  - `networkScan`: the effective scan is not GREEN (ORANGE, RED or not
 *    documented) — show the reason and the guide.
 *  - `structural`: the scan is GREEN, so the "no" is structural (asset inside
 *    a gateway system, non-current location). Callers keep today's behaviour
 *    and explanations (e.g. the wizard's partOfIotSystem check).
 */
export type IotAttachGateReason = 'networkScan' | 'structural' | null;

export interface IotAttachGateResult {
    /** Still waiting for the gate query; render nothing decisive yet. */
    pending: boolean;
    /** The backend allows a new attach (or the feature is off). */
    allowed: boolean;
    reason: IotAttachGateReason;
    scan: EffectiveNetworkScanRef | null;
}

export const resolveIotAttachGate = (input: IotAttachGateInput): IotAttachGateResult => {
    const scan = input.effectiveNetworkScan ?? null;
    if (!input.enabled) return { pending: false, allowed: true, reason: null, scan: null };
    if (!input.loaded) return { pending: true, allowed: false, reason: null, scan };
    if (input.canAttachIotConnection) return { pending: false, allowed: true, reason: null, scan };
    if (isNetworkScanPassed(scan?.state)) return { pending: false, allowed: true, reason: 'structural', scan };
    return { pending: false, allowed: false, reason: 'networkScan', scan };
};
